import { archiveProjects } from './archiveProjects';

/**
 * Registry dei 3 progetti in homepage + navigazione next/prev.
 * Immagini in /public/projects/{slug}/
 */
export const projects = [
  {
    slug: 'menya',
    path: '/menya',
    index: '01',
    title: 'Menya Noodle',
    stripLabel: 'Hybrid Content Production · F&B',
    studioLine: 'Manila · Social Content · 2026',
    category: 'Hybrid Content Production · F&B',
    year: '2026',
    tagline: '',
    heroImage: '/projects/menya/menya-frame-01.webp',
    heroLayout: 'overlay',
    introVariant: 'spacious',
    introHeader: {
      index: '001',
      stripLabel: 'Hybrid Content Production · F&B',
      studioLine: 'Manila · Social Content · 2026',
      tagline:
        'Hybrid content campaign for a Manila ramen brand: real shot subjects composited into AI-generated environments.',
    },
    next: { slug: 'nordvik', path: '/nordvik', title: 'Nordvik' },
    meta: {
      client: 'Menya Noodle Manila',
      role: 'AI Art Direction · Hybrid Content Production',
      format: 'Social Media Campaign',
      year: '2026',
      deliverables: '8 Hero Shots · Social-ready Visuals',
      process: 'Subject Isolation · Background Plate Generation · Color Grading',
    },
    intro: [
      'Commissioned by Menya Noodle Manila to produce a complete social content campaign for a Japanese ramen restaurant in the Philippines.',
      'The brief required eight campaign-ready frames combining real shot subjects with fully AI-generated environments, with consistent lighting, brand presence and food presentation across the full set, without on-set production.',
    ],
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'pair',
        portrait: true,
        left: {
          src: '/projects/menya/menya-frame-02.webp',
          alt: 'Subject — warm tungsten · neon background plate',
          caption: 'SHOT 01 — Subject · Warm tungsten · Neon background plate',
        },
        right: {
          src: '/projects/menya/menya-frame-03.webp',
          alt: 'Top down — product detail · natural light',
          caption: 'SHOT 02 — Top down · Product detail · Natural light',
        },
      },
      {
        type: 'process',
        layout: 'narrow',
        title: 'Process note',
        paragraphs: [
          'Visual language and shot list were defined before any generation pass: color temperature, practical light placement, background depth and brand signage position established upfront.',
          'Real shot subjects were isolated and composited into AI-generated background plates, with tungsten fill, neon color grading and volumetric atmosphere matched per frame.',
          'Subject consistency, focal length and lighting ratio were maintained across all eight frames through iterative art direction, ensuring a cohesive set from a zero-crew production.',
        ],
      },
      {
        type: 'grid',
        rows: [
          {
            left: {
              src: '/projects/menya/menya-frame-04.webp',
              alt: 'Close up — subject expression · shallow depth of field',
              caption: 'SHOT 03 — Close up · Subject expression · Shallow DOF',
            },
            right: {
              src: '/projects/menya/menya-frame-05.webp',
              alt: 'Brand signage — neon presence · night plate',
              caption: 'SHOT 04 — Brand signage · Neon presence · Night plate',
            },
          },
          {
            left: {
              src: '/projects/menya/menya-frame-04.webp',
              alt: 'Product hero — ramen · macro framing',
              caption: 'SHOT 05 — Product hero · Ramen · Macro framing',
            },
            right: {
              src: '/projects/menya/menya-frame-06.webp',
              alt: 'Subject — lateral view · ambient fill',
              caption: 'SHOT 06 — Subject · Lateral view · Ambient fill',
            },
          },
        ],
      },
      {
        type: 'full',
        variant: 'bleed',
        src: '/projects/menya/menya-frame-06.webp',
        alt: 'Restaurant interior — wide · ambient scene',
        caption: 'SHOT 07 — Restaurant interior · Wide · Ambient scene',
      },
      {
        type: 'full',
        variant: 'centered',
        src: '/projects/menya/menya-frame-05.webp',
        alt: 'Subject — full background plate · brand environment',
        caption: 'SHOT 08 — Subject · Full background plate · Brand environment',
      },
    ],
    discipline: 'Hybrid Content · F&B',
    blurb:
      'Eight-shot social campaign for Menya Noodle Manila: hybrid real shot and AI environments, zero-crew production.',
    media: '/projects/menya/menya-frame-01.webp',
  },
  {
    slug: 'nordvik',
    path: '/nordvik',
    index: '02',
    title: 'Nordvik',
    stripLabel: 'Cinematic LookDev · Visual Direction',
    studioLine: 'Norwegian Cinematic Studio · 2025',
    category: 'Cinematic LookDev · Visual Direction',
    year: '2025',
    tagline:
      'Visual development for an unannounced pilot episode: cinematic lookdev and production references built through AI-assisted generation.',
    heroImage: '/projects/untitled/nordvik-frame-01.webp',
    heroLayout: 'fullscreen',
    introVariant: 'spacious',
    introHeader: {
      index: '002',
      stripLabel: 'Cinematic LookDev · Visual Direction',
      studioLine: 'Norwegian Cinematic Studio · 2025',
      tagline:
        'Visual development for an unannounced pilot episode: cinematic lookdev and production references built through AI-assisted generation.',
    },
    next: { slug: 'solitaire', path: '/solitaire', title: 'Solitaire' },
    meta: {
      client: 'Norwegian Cinematic Studio',
      role: 'Cinematic LookDev · AI Art Direction',
      format: 'Pilot Episode · Unannounced',
      year: '2025',
      deliverables: 'Hero Shots · Scene References · Visual Direction',
      process: 'LookDev · Palette Lock · Focal Length Control · DOP Reference',
    },
    intro: [
      'Commissioned by a Norwegian cinematic studio to develop the visual language for an unannounced pilot episode.',
      'The brief required establishing a complete lookdev package before principal photography: hero shots and scene references delivered to define palette, lighting ratios and lens treatment for the director and DOP.',
    ],
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'shot',
        src: '/projects/untitled/nordvik-frame-02.webp',
        alt: 'Character intro — coastal overcast · diffused fill',
        caption: 'FRAME 01 — Character intro · Coastal overcast · Diffused fill',
        variant: 'centered',
        portrait: true,
      },
      {
        type: 'process',
        layout: 'narrow',
        title: 'Process note',
        paragraphs: [
          'Each frame was built around a defined lighting setup: diffused overcast exterior, cold fill and selective focus used consistently across all frames to establish lens and atmosphere.',
          'Color palette was locked upfront: desaturated coastal tones, compressed highlights and lifted shadows defining the visual signature of the series. Applied uniformly across every generation pass through controlled art direction.',
          'Every output was produced to function as a production reference, with framing, focal length and color grade consistent enough to hand directly to a DOP.',
        ],
      },
      {
        type: 'pair',
        portrait: true,
        left: {
          src: '/projects/untitled/nordvik-frame-03.webp',
          alt: 'Close up — natural light · compressed highlights',
          caption: 'FRAME 02 — Close up · Natural light · Compressed highlights',
        },
        right: {
          src: '/projects/untitled/nordvik-frame-04.webp',
          alt: 'Profile — cliffside · cold fill · shallow depth of field',
          caption: 'FRAME 03 — Profile · Cliffside · Cold fill · Shallow DOF',
        },
      },
      {
        type: 'shot',
        src: '/projects/untitled/nordvik-frame-05.webp',
        alt: 'Three quarter — coastal environment · full palette reference',
        caption: 'FRAME 04 — Three quarter · Coastal env · Full palette reference',
        variant: 'centered',
      },
    ],
    discipline: 'Cinematic LookDev · Visual Direction',
    blurb:
      'Pilot lookdev for a Norwegian cinematic studio. AI-assisted hero shots and DOP-ready scene references with locked palette and focal length.',
    media: '/projects/untitled/nordvik-frame-01.webp',
  },
  {
    slug: 'solitaire',
    path: '/solitaire',
    index: '03',
    title: 'Solitaire',
    stripLabel: '3D Rendering · Product Visualization',
    studioLine: 'Leading French Jewelry Retailer · 2025',
    category: '3D Rendering · Product Visualization',
    year: '2025',
    tagline:
      'Six-angle product visualization for a leading French jewelry retailer: geometry, materials and lighting built for catalog and e-commerce.',
    heroImage: '/projects/solitaire/solitaire-frame-01.webp',
    heroLayout: 'overlay',
    introVariant: 'spacious',
    introHeader: {
      index: '003',
      stripLabel: '3D Rendering · Product Visualization',
      studioLine: 'Leading French Jewelry Retailer · 2025',
      tagline:
        'Six-angle product visualization for a leading French jewelry retailer: geometry, materials and lighting built for catalog and e-commerce.',
    },
    next: { slug: 'rain-alley', path: '/rain-alley', title: 'Rain Alley' },
    meta: {
      client: 'Leading French Jewelry Retailer',
      role: '3D Modeling · Rendering · Art Direction',
      format: 'Product Visualization',
      year: '2025',
      deliverables: '6 Hero Renders · Multi-angle Set',
      process: 'High-poly Modeling · PBR Materials · HDRI Lighting · Diamond Shader',
    },
    intro: [
      'Commissioned to produce a multi-angle visualization set for a solitaire diamond engagement ring, a flagship piece for one of France\'s leading jewelry retail chains.',
      'The brief required six distinct angles at catalog quality: precise diamond shader, controlled studio lighting and a neutral surface throughout. Production-ready output for digital catalog and e-commerce deployment.',
    ],
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'full',
        variant: 'bleed',
        src: '/projects/solitaire/solitaire-frame-02.webp',
        alt: 'Hero shot — three quarter · studio lighting',
        caption: 'ANGLE 01 — Hero shot · Three quarter · Studio lighting',
      },
      {
        type: 'process',
        layout: 'narrow',
        title: 'Process note',
        paragraphs: [
          'Every render was built from scratch: high-poly geometry, physically-based materials and HDRI studio lighting defined to match the visual standards of luxury jewelry photography.',
          'Diamond shader was calibrated for accurate light refraction and brilliance across all six angles. A single three-point lighting setup was locked and maintained throughout the set to ensure material consistency across every view.',
          'The six angles cover the full commercial range: hero presentation, prong detail, band structure, profile and e-commerce flat, all output at catalog resolution.',
        ],
      },
      {
        type: 'grid',
        rows: [
          {
            left: {
              src: '/projects/solitaire/solitaire-frame-03.webp',
              alt: 'Prong detail — macro · diamond refraction',
              caption: 'ANGLE 02 — Prong detail · Macro · Diamond refraction',
            },
            right: {
              src: '/projects/solitaire/solitaire-frame-04.webp',
              alt: 'Top down — shadow study · neutral surface',
              caption: 'ANGLE 03 — Top down · Shadow study · Neutral surface',
            },
          },
          {
            left: {
              src: '/projects/solitaire/solitaire-frame-05.webp',
              alt: 'Frontal — band structure · PBR material',
              caption: 'ANGLE 04 — Frontal · Band structure · PBR material',
            },
            right: {
              src: '/projects/solitaire/solitaire-frame-06.webp',
              alt: 'Lateral profile — full ring geometry',
              caption: 'ANGLE 05 — Lateral profile · Full ring geometry',
            },
          },
        ],
      },
    ],
    discipline: '3D Rendering · Product Visualization',
    blurb:
      'Six-angle solitaire set for a leading French jewelry retailer, with PBR materials, diamond shader and HDRI lighting at catalog resolution.',
    media: '/projects/solitaire/solitaire-frame-01.webp',
  },
];

const SLUG_ALIASES = {
  untitled: 'nordvik',
  artificial: 'nordvik',
  ramen: 'menya',
  synthesis: 'menya',
  'figure-f.03': 'solitaire',
  'project-4': 'rain-alley',
  'quantum-drift': 'rain-alley',
  'project-5': 'wraith',
  'la-creatura': 'wraith',
  'project-6': 'shelter',
  shelter: 'shelter',
  'project-7': 'bar-scene',
  'bar-scene': 'bar-scene',
  'project-8': 'dojaka',
  dojaka: 'dojaka',
  underground: 'dojaka',
  'project-9': 'klm-board',
  'klm-board': 'klm-board',
  'temporal-layers': 'klm-board',
  'project-10': 'dior-board',
  'dior-board': 'dior-board',
  'project-11': 'mismo-studio',
  'mismo-studio': 'mismo-studio',
  'project-12': 'mescalina',
  mescalina: 'mescalina',
};

const allProjects = [...projects, ...archiveProjects];

export const getProjectBySlug = (slug) => {
  const resolved = SLUG_ALIASES[slug] || slug;
  return allProjects.find((p) => p.slug === resolved);
};

export const getProjectByPath = (path) => allProjects.find((p) => p.path === path);
